/**
 * Meta Pixel Sprint 2026-08 — central wrapper.
 *
 * The ONLY module in the codebase that touches ``window.fbq``. Every
 * event surface calls this wrapper's ``track()`` — direct
 * ``window.fbq(...)`` calls elsewhere are forbidden (enforceable via
 * a follow-up lint rule if needed).
 *
 * Gate matrix (all four must be true for a real load / event):
 *
 *   * ``REACT_APP_META_PIXEL_ID`` non-empty        — env config
 *   * ``NODE_ENV === "production"``                — no preview / test
 *   * ``getMarketingConsent() === true``           — DSGVO gate
 *   * ``navigator.doNotTrack`` not "1"             — hard DNT respect
 *
 * If any gate is false → ``init()`` is a no-op and ``track()`` is a
 * no-op. Missing configuration NEVER throws.
 *
 * Datenschutz guardrails baked into ``track()``:
 *
 *   * ``fbq('set', 'autoConfig', false, ID)`` runs BEFORE any track
 *     so Meta's automatic microdata harvesting stays off.
 *   * ``fbq('consent', 'revoke')`` is issued on ``disableTracking()``
 *     and again on the ``storage`` event that signals a revoke in
 *     another tab.
 *   * Advanced Matching is explicitly NOT initialised — the second
 *     argument to ``fbq('init', id)`` is deliberately omitted.
 *
 * URL sanitisation — before every ``fbq('track', 'PageView')`` we
 * strip a fixed allowlist of query-string keys that could otherwise
 * leak PII into ``document.location.href`` (Meta reads it directly).
 */
import { getMarketingConsent, subscribe as subscribeConsent } from "../consent/consent";

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const IS_PROD = process.env.NODE_ENV === "production";
const SDK_URL = "https://connect.facebook.net/en_US/fbevents.js";

/**
 * Routes we ABSOLUTELY never emit a PageView on. Meta reads
 * ``document.location.href`` internally, so any of these paths would
 * either leak a one-time token (`/verify-email`, `/reset-password`,
 * auth callbacks) or race the referral-system's URL cleanup
 * (``?ref=`` present). The rule from the P0.5 review:
 *
 *   > Analytics-Code darf niemals operative URL-Parameter aus der
 *   > echten App-URL löschen. […] Auf sensiblen Routen wie
 *   > /verify-email, /reset-password und Auth-Callbacks überhaupt
 *   > keinen Pixel-PageView senden.
 *
 * The list is deny-only — every other route is safe by default.
 */
const SENSITIVE_ROUTE_PREFIXES = [
  "/verify-email",
  "/verify-pending",
  "/verify-success",
  "/reset-password",
  "/forgot-password",
  "/auth/callback",
  "/oauth/callback",
];

/**
 * Query-string keys that, when present in the URL, defer the
 * PageView until after the app's own routing has processed them.
 * We DO NOT touch the URL ourselves — the referral system persists
 * the code into ``sessionStorage`` and the auth flows consume their
 * tokens; when they replace the URL the next route change (or the
 * consent grant callback) fires the PageView cleanly.
 */
const DEFER_PAGEVIEW_QUERY_KEYS = [
  "ref", "referral", "referral_code", "invite",
  "token", "access_token", "refresh_token", "code", "state",
  "session_id", "email",
];

let _initialised = false;
let _consentUnsub = null;

function _canTrack() {
  if (!PIXEL_ID) return false;
  if (!IS_PROD) return false;
  if (
    typeof navigator !== "undefined"
    && (navigator.doNotTrack === "1" || navigator.msDoNotTrack === "1")
  ) {
    return false;
  }
  return getMarketingConsent();
}

function _injectSdk() {
  if (typeof window === "undefined") return;
  if (window.fbq) return;
  // Meta's official pixel bootstrap snippet — deliberately kept in
  // its canonical shape so `fbq` behaves exactly like Meta's docs
  // describe. The IIFE assigns `window.fbq` as a queue that flushes
  // once fbevents.js loads and calls back.
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", SDK_URL);
}

function _isSensitiveRoute(pathname) {
  if (typeof pathname !== "string" || !pathname) return false;
  return SENSITIVE_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function _hasDeferQueryKey(search) {
  if (!search) return false;
  try {
    const params = new URLSearchParams(search);
    return DEFER_PAGEVIEW_QUERY_KEYS.some((k) => params.has(k));
  } catch {
    return false;
  }
}

/**
 * Public — determines whether the CURRENT ``window.location`` is a
 * safe place to fire a Meta PageView. The rule is read-only: we
 * never mutate the URL, we only decide whether to skip the emit for
 * this render.
 *
 * Returns ``false`` (i.e. skip) when:
 *   * the pathname is a sensitive route (verify-email, reset,
 *     auth-callback, ...)
 *   * any deferrable query key is present (``ref``, ``token``,
 *     ``code``, ``state``, ...) — we wait until the app has
 *     consumed and cleaned them from the URL itself.
 */
export function isPageViewAllowed() {
  if (typeof window === "undefined") return false;
  const { pathname = "", search = "" } = window.location || {};
  if (_isSensitiveRoute(pathname)) return false;
  if (_hasDeferQueryKey(search)) return false;
  return true;
}

/** Public — initialise the pixel exactly once. Safe to call
 *  repeatedly; the wrapper de-dupes internally. */
export function initMetaPixel() {
  if (_initialised) return;
  if (!_canTrack()) return;
  _injectSdk();
  window.fbq("set", "autoConfig", false, PIXEL_ID);
  // Deliberately omit the second `advancedMatchingOptions` arg — the
  // Sprint contract forbids Advanced Matching. This is the ONE place
  // where that decision must be visible in code.
  window.fbq("init", PIXEL_ID);
  window.fbq("consent", "grant");
  _initialised = true;

  // Attach the revocation listener exactly once. A consent flip in
  // another tab (storage event) OR in this tab (in-process
  // subscribe) must immediately stop future events.
  if (!_consentUnsub) {
    _consentUnsub = subscribeConsent((c) => {
      if (!c.decided || !c.categories.marketing) disableTracking();
    });
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (e) => {
        if (e.key === "dealalert_consent_v1" && !getMarketingConsent()) {
          disableTracking();
        }
      });
    }
  }
}

/** Public — revoke consent at the pixel layer. `fbq` remains defined
 *  so future callers stay no-op-safe, but every subsequent event is
 *  dropped by the fbq consent gate. */
export function disableTracking() {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("consent", "revoke");
  } catch {
    /* fbq not fully loaded yet — no-op is safe */
  }
  _initialised = false;
}

/** Public — the single event surface. Missing consent / config /
 *  init → no-op. Never throws. */
export function track(eventName, params) {
  if (!eventName) return;
  if (!_canTrack()) return;
  if (!_initialised) initMetaPixel();
  if (!_initialised) return; // still not initialised → bail
  const safeParams = _sanitizeParams(params);
  try {
    window.fbq("track", eventName, safeParams);
  } catch {
    /* fbq raised → do NOT propagate, tracking is best-effort */
  }
}

/** Public — dedicated PageView entry. Read-only URL check: skips
 *  sensitive routes and deferrable query keys instead of ever
 *  mutating the URL itself (P0.5 review — the referral system and
 *  the auth flows own URL cleanup). */
export function trackPageView() {
  if (!_canTrack()) return;
  if (!isPageViewAllowed()) return;
  track("PageView");
}

/** Whitelist that guards the shape of the outgoing parameters. Any
 *  key not listed here is dropped — this is how we structurally
 *  guarantee "no PII to Meta". Only the params Meta's Standard
 *  Events actually understand are allowed through. */
const ALLOWED_PARAM_KEYS = new Set([
  "content_ids", "content_type", "content_category", "content_name",
  "currency", "value",
]);

function _sanitizeParams(params) {
  if (!params || typeof params !== "object") return undefined;
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (!ALLOWED_PARAM_KEYS.has(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === "string") {
      // Truncate defensively — Meta rejects huge strings and we
      // don't want to send anything long anyway.
      out[k] = v.slice(0, 256);
    } else if (Array.isArray(v)) {
      out[k] = v.filter((x) => typeof x === "string").slice(0, 20);
    } else if (typeof v === "number" && Number.isFinite(v)) {
      out[k] = v;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

/** Testing helpers. */
export function _isInitialisedForTests() {
  return _initialised;
}
export function _resetForTests() {
  _initialised = false;
  if (_consentUnsub) {
    _consentUnsub();
    _consentUnsub = null;
  }
  if (typeof window !== "undefined") {
    delete window.fbq;
    delete window._fbq;
  }
}
export function _getGateStateForTests() {
  return {
    pixelId: PIXEL_ID,
    isProd: IS_PROD,
    canTrack: _canTrack(),
    initialised: _initialised,
  };
}
